import { useCallback, useMemo } from 'react';

import { AgGridServerTable } from 'core/components';
import { TableLayoutType } from 'core/enums';
import { AgGridServerTableProvider, SelectionCountProvider } from 'core/states';
import { AgGridGetRowsParams, ISelectionActionProps, ITableActionProps } from 'core/types';
import {
  RemovePricingProfileAccessButton,
  UpsertPricingProfileAccessButton,
} from 'pricing/components';
import { accessService } from 'pricing/services';
import { IPricingProfile, IPricingProfileAccessItem } from 'pricing/types';

import { getColumnDef } from './columns';

interface IProps {
  pricingProfile: IPricingProfile;
  isReadOnly?: boolean;
}

export const AccessList = ({ isReadOnly = false, pricingProfile }: IProps) => {
  const defaultColDef = useMemo(
    () => ({
      autoHeight: true,
    }),
    []
  );
  const columnDefs = useMemo(() => getColumnDef({ isEditable: !isReadOnly }), [isReadOnly]);

  const actionButtons = useCallback(
    ({ gridApi }: ITableActionProps) => {
      if (isReadOnly) {
        return [];
      }

      return [
        <UpsertPricingProfileAccessButton
          key="upsertPricingProfileAccessButton"
          pricingProfileId={pricingProfile.id}
          onSubmitSuccess={() => gridApi?.refreshServerSide()}
        />,
      ];
    },
    [isReadOnly, pricingProfile.id]
  );

  const selectionActionButtons = useCallback(
    ({ gridApi, selectedRows, deselectAllRows }: ISelectionActionProps) => {
      if (isReadOnly) {
        return [];
      }

      return [
        <RemovePricingProfileAccessButton
          key="removeSelectedAccessButton"
          pricingProfileAccessItems={selectedRows as IPricingProfileAccessItem[]}
          onRemoveSuccess={() => {
            deselectAllRows();
            gridApi?.refreshServerSide();
          }}
        />,
      ];
    },
    [isReadOnly]
  );

  const getRows = useCallback(
    ({ filters, ...rest }: AgGridGetRowsParams) => {
      return accessService.getPricingProfileAccessItems({
        filters: {
          ...filters,
          pricingProfileId: pricingProfile.id,
        },
        ...rest,
      });
    },
    [pricingProfile.id]
  );

  return (
    <SelectionCountProvider>
      <AgGridServerTableProvider
        tableId="pricingProfileAccessTable"
        entityType="AccessItem"
        defaultColDef={defaultColDef}
        columnDefs={columnDefs}
        getRows={getRows}
        hasLayoutActions
        hasManageFilters
        layoutType={TableLayoutType.PRICING_PROFILE_ACCESS}
        isRowSelectable={() => !isReadOnly}
      >
        <AgGridServerTable
          actionButtons={actionButtons}
          selectionActionButtons={selectionActionButtons}
        />
      </AgGridServerTableProvider>
    </SelectionCountProvider>
  );
};
